// =============================================================================
// Route Management – API layer
// Load endpoint:  GET /api/perfect-truck/routes/all (page 0, size 100)
// Stats endpoint: GET /api/perfect-truck/routes/statistics
// =============================================================================

import { createApiClient, getLocalTimestamp, getTimezoneOffsetString } from '../../../../api/apiClient';
import { Route, RouteListResponse, RouteStats } from './routeManagementTypes';

const getClientTimeParams = () => ({
  clientTimestamp: getLocalTimestamp(),
  clientTimezone: getTimezoneOffsetString(),
});

const BASE = '/perfect-truck/routes';

// ─── Params for paginated / filtered list fetch ────────────────────────────────

export interface FetchRoutesParams {
  page?: number;
  size?: number;
  sort?: string;
  locationName?: string;
  searchTerm?: string;      // partial match: routeNumber, routeType, locationName, sapId
  routeTypes?: string[];    // multi exact-match; repeated keys: ?routeTypes=A&routeTypes=B
  isActiveList?: boolean[]; // multi exact-match; repeated keys: ?isActiveList=true&isActiveList=false
}

// ─── Single page fetch ────────────────────────────────────────────────────────

/** Serialize params so arrays become repeated keys: ?key=a&key=b (not key[]=a). */
const serializeParams = (p: Record<string, any>): string => {
  const parts: string[] = [];
  for (const [key, value] of Object.entries(p)) {
    if (Array.isArray(value)) {
      value.forEach((v: any) => parts.push(`${encodeURIComponent(key)}=${encodeURIComponent(v)}`));
    } else if (value !== undefined && value !== null) {
      parts.push(`${encodeURIComponent(key)}=${encodeURIComponent(value)}`);
    }
  }
  return parts.join('&');
};

export const fetchRoutes = async (params: FetchRoutesParams = {}): Promise<RouteListResponse> => {
  const {
    page = 0,
    size = 20,
    sort = 'locationId,asc',
    locationName,
    searchTerm,
    routeTypes,
    isActiveList,
  } = params;

  const queryParams: Record<string, any> = { page, size, sort };
  if (locationName?.trim()) queryParams.locationName = locationName.trim();
  if (searchTerm?.trim()) queryParams.searchTerm = searchTerm.trim();
  if (routeTypes?.length) queryParams.routeTypes = routeTypes;
  if (isActiveList?.length) queryParams.isActiveList = isActiveList;

  const api = createApiClient();
  const res = await api.get<RouteListResponse>(`${BASE}/all`, {
    params: queryParams,
    paramsSerializer: serializeParams,
  });
  return res.data;
};

// ─── Fetch first page (page 0, size 100) for screen initial load ───────────────

export const fetchAllRoutes = async (): Promise<Route[]> => {
  const api = createApiClient();
  const res = await api.get<RouteListResponse>(`${BASE}/all`, {
    params: { page: 0, size: 100, sort: 'locationId,asc' },
  });
  return res.data.data;
};

// ─── Dashboard card stats (from dedicated statistics endpoint) ────────────────
// Lightweight call that returns pre-aggregated counts from backend.

interface StatisticsResponse {
  activeRouteCount: number;
  inactiveRouteCount: number;
  totalRouteCount: number;
  totalLocationCount: number;
}

export const fetchRouteStats = async (): Promise<RouteStats> => {
  const api = createApiClient();
  const res = await api.get<StatisticsResponse>(`${BASE}/statistics`);
  return {
    totalRoutes: res.data.totalRouteCount,
    locationCount: res.data.totalLocationCount,
    activeRoutes: res.data.activeRouteCount,
  };
};

// ─── Update route type inline ─────────────────────────────────────────────────
// PATCH /api/perfect-truck/locations/{locationId}/routes/{id}/route-type
// All inputs are query params — no request body.

export const updateRouteType = async (
  id: number,
  locationId: string,
  routeType: string,
  requestedBy: string,
): Promise<Route> => {
  const api = createApiClient();
  const res = await api.patch<{ data: Route }>(
    `/perfect-truck/locations/${locationId}/routes/${id}/route-type`,
    null,
    {
      params: {
        routeType,
        requestedBy,
        ...getClientTimeParams(),
      },
    },
  );
  return res.data.data;
};

// ─── Toggle route active status ───────────────────────────────────────────────

export const toggleRouteActive = async (
  id: number,
  locationId: string | number,
  isActive: boolean,
  requestedBy: string,
): Promise<Route> => {
  const api = createApiClient();
  const res = await api.patch<{ data: Route }>(
    `/perfect-truck/locations/${locationId}/routes/${id}/toggle-active`,
    { isActive, requestedBy },
    { params: getClientTimeParams() },
  );
  return res.data.data;
};

// ─── Fetch active route type options from config ───────────────────────────────

export const fetchActiveRouteTypes = async (): Promise<{ label: string; value: string }[]> => {
  const api = createApiClient();
  const res = await api.get<{ data: Array<{ routeType: string }> }>(
    '/perfect-truck/config/route-types',
    { params: { isActive: true } },
  );
  return res.data.data.map(rt => ({ label: rt.routeType, value: rt.routeType }));
};

/** Fetches ALL route types regardless of active status — used in analytics/reporting filters. */
export const fetchAllRouteTypes = async (): Promise<{ label: string; value: string }[]> => {
  const api = createApiClient();
  const res = await api.get<{ data: Array<{ routeType: string }> }>(
    '/perfect-truck/config/route-types',
  );
  return res.data.data.map(rt => ({ label: rt.routeType, value: rt.routeType }));
};
